import { Stack, Typography } from '@mui/material';
import { first } from 'lodash';

import { Button } from '@/components/form/baseInputs/Button';
import { FormAwareSelectInput } from '@/components/form/formAwareInputs/FormAwareSelectInput';
import { FormAwareTextInput } from '@/components/form/formAwareInputs/FormAwareTextInput';
import { ModalFormLayout } from '@/components/form/layout/ModalFormLayout';
import { FormModal } from '@/components/modals/FormModal/FormModal';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import {
  FormProvider,
  useForm,
  useFormContext,
} from '@/components/react-hook-form';
import { useReportError } from '@/hooks/useReportError';
import { makeCreateEntityInput } from '@/modules/entities/EntityShortFormModal/CreateEntityShortForm/formUtils/CreateEntityShortForm.entityUtils';
import { adaptFormValuesToEntityShortFormShape } from '@/modules/estate/EstateDirectHoldings/DirectHoldingsGroupContainer/AddNewHoldingsGroupFormModal/AddNewHoldingsGroupFormModal.utils';
import { Operations } from '@/modules/estate/graphql/EstatePageData.generated';
import {
  ContextPrimaryClient,
  useHouseholdDetailsContext,
} from '@/modules/household/contexts/householdDetails.context';

import { DirectHoldingsGroupVariant } from '../../EstateDirectHoldings.types';
import {
  ADD_NEW_HOLDINGS_OPTIONS_BY_VARIANT,
  AddNewHoldingsGroupFormShape,
} from './AddNewHoldingsGroupFormModal.types';
import { useAddNewHoldingsGroupMutation } from './graphql/AddNewHoldingsGroupFormModal.generated';

export interface AddNewHoldingsGroupFormModalProps {
  variant: DirectHoldingsGroupVariant;
  clientProfile?: ContextPrimaryClient;
  open: boolean;
  onClose: () => void;
}

interface AddNewHoldingsGroupFormProps {
  variant: DirectHoldingsGroupVariant;
  clientProfile?: ContextPrimaryClient;
}

function AddNewHoldingsGroupForm({
  clientProfile,
  variant,
}: AddNewHoldingsGroupFormProps) {
  const { control } = useFormContext<AddNewHoldingsGroupFormShape>();

  let description;
  switch (variant) {
    case 'individual':
      description = `Create a holdings group for ${clientProfile?.displayName}`;
      break;
    case 'joint':
      description = `Create a joint holdings group`;
      break;
    case 'other':
    default:
      description = 'Create a holdings group';
      break;
  }

  return (
    <ModalFormLayout>
      <Stack spacing={2}>
        <Typography variant="body-s">
          {description}
          —each card is displayed as a tile in Waterfall diagrams.
        </Typography>

        {variant != 'joint' && (
          <FormAwareSelectInput
            control={control}
            fieldName="type"
            label="Type"
            options={ADD_NEW_HOLDINGS_OPTIONS_BY_VARIANT[variant]}
            required
          />
        )}
        <FormAwareTextInput
          control={control}
          fieldName="name"
          label="Name"
          required
        />
      </Stack>
    </ModalFormLayout>
  );
}

export function AddNewHoldingsGroupFormModal({
  variant,
  clientProfile,
  open,
  onClose,
}: AddNewHoldingsGroupFormModalProps) {
  const formMethods = useForm<AddNewHoldingsGroupFormShape>({
    defaultValues: {
      type: variant === 'joint' ? 'joint-account' : null,
      name: null,
    },
  });

  const { householdId } = useHouseholdDetailsContext();
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();
  const { primaryClients } = useHouseholdDetailsContext();

  const [addNewHoldingsGroup, { loading }] = useAddNewHoldingsGroupMutation({
    onCompleted: () => {
      showFeedback('Holdings group created successfully', {
        variant: 'success',
      });
      onClose();
    },
    onError: (error) => {
      reportError('Could not create holdings group', error as Error);
      showFeedback('Failed to create holdings group. Please try again.');
    },
    refetchQueries: [Operations.Query.EstatePageData],
  });

  const onValidSubmission = async (data: AddNewHoldingsGroupFormShape) => {
    if (!householdId) {
      reportError('Household ID is required to create a direct holding');
      return;
    }
    if (!primaryClients) {
      reportError('Primary client is required to create a direct holding');
      return;
    }
    try {
      // When creating from an individual client's section, only use that client as the principal.
      // Otherwise (joint/other), use all primary clients.
      const principalIds = clientProfile
        ? [clientProfile.id]
        : primaryClients.map((pc) => pc.id);
      const input = makeCreateEntityInput(
        adaptFormValuesToEntityShortFormShape(
          data,
          principalIds,
          // if principal not provided, default to first primary client (ex: custodial & tuition accounts)
          clientProfile?.id ?? first(principalIds)
        ),
        householdId,
        {
          associatedDocumentIds: [],
        }
      );
      await addNewHoldingsGroup({
        variables: { input },
      });
    } catch (error) {
      reportError('Could not create holdings group', error as Error);
      showFeedback('Failed to create holdings group. Please try again.');
    }
  };

  const onSubmit = formMethods.handleSubmit(onValidSubmission);

  return (
    <FormProvider {...formMethods}>
      <FormModal
        heading="Create holdings group"
        isOpen={open}
        onClose={onClose}
        actions={
          <>
            <Button
              variant="secondary"
              size="sm"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={onSubmit}
              loading={loading}
            >
              Create holdings group
            </Button>
          </>
        }
      >
        <AddNewHoldingsGroupForm
          variant={variant}
          clientProfile={clientProfile}
        />
      </FormModal>
    </FormProvider>
  );
}
